/**
 * "Install the app" (D-63). Chrome/Edge on Android and desktop fire `beforeinstallprompt`; we keep
 * it and show our own button. iPhone Safari has no such event — the shopper adds the app from the
 * Share menu, so we show those steps instead.
 */
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import { persistStorage } from '@/lib/storage'

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

interface InstallState {
  /** The browser's saved install prompt, when it offered one. */
  deferred: BeforeInstallPromptEvent | null
  /** Installed here (browser said so) or, on iPhone, the shopper tapped "I've added it". */
  installed: boolean
  /** The install sheet already showed once on this device; after that only the menu offers it. */
  promptShown: boolean
  markInstalled: () => void
  markPromptShown: () => void
}

export const useInstallStore = create<InstallState>()(
  persist(
    (set) => ({
      deferred: null,
      installed: false,
      promptShown: false,
      markInstalled: () => set({ installed: true }),
      markPromptShown: () => set({ promptShown: true }),
    }),
    {
      name: 'angadi.install',
      storage: createJSONStorage(() => persistStorage),
      version: 1,
      partialize: (s) => ({ installed: s.installed, promptShown: s.promptShown }),
    },
  ),
)

/** Call once at start-up, before the first render, so the early browser event isn't missed. */
export function listenForInstall(): void {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    // Only offered while the app isn't installed — e.g. after an uninstall.
    useInstallStore.setState({ deferred: e as BeforeInstallPromptEvent, installed: false })
  })
  window.addEventListener('appinstalled', () => {
    useInstallStore.setState({ deferred: null, installed: true })
  })
}

/** Opened from the home-screen icon (Android, desktop, or iPhone). */
export function isStandalone(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean }
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true || nav.standalone === true
  )
}

/** iPhone/iPad, where installing is "Share → Add to Home Screen". */
export function isIos(ua = navigator.userAgent, touchPoints = navigator.maxTouchPoints): boolean {
  return /iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && touchPoints > 1)
}

/** Show the browser's install dialog; true when the shopper accepted. */
export async function promptInstall(): Promise<boolean> {
  const { deferred } = useInstallStore.getState()
  if (!deferred) return false
  await deferred.prompt()
  const { outcome } = await deferred.userChoice
  useInstallStore.setState({ deferred: null, installed: outcome === 'accepted' })
  return outcome === 'accepted'
}

/** How this device can install: a button, the iPhone steps, or not at all. */
export function useInstallMode(): 'prompt' | 'ios' | null {
  const deferred = useInstallStore((s) => s.deferred)
  const installed = useInstallStore((s) => s.installed)
  if (isStandalone()) return null
  if (deferred) return 'prompt'
  return !installed && isIos() ? 'ios' : null
}
